import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  ThemeProvider as MuiThemeProvider,
  darkTheme as MuiDarkTheme,
  Box,
} from '@locus-taxy/encore-mui';
import { useTeamSelection } from '../hooks/useTeamSelection';
import type { Team } from '../types/team';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';

const ALL_TEAMS_OPTION = { id: 'all', name: 'All Teams', color: 'transparent' };

export default function TeamSelect() {
  const { selectedTeams, allTeams, updateSelectedTeams } = useTeamSelection();
  const [tempSelectedTeams, setTempSelectedTeams] =
    useState<typeof allTeams>(selectedTeams);

  // Update temp state when selectedTeams prop changes
  useEffect(() => {
    setTempSelectedTeams(selectedTeams);
  }, [selectedTeams]);

  // Options for Autocomplete: All Teams + all teams (no grouping)
  const options = useMemo<(typeof ALL_TEAMS_OPTION | Team)[]>(
    () => [ALL_TEAMS_OPTION, ...allTeams],
    [allTeams]
  );

  // Handle selection changes
  const handleChange = useCallback(
    (
      _event: unknown,
      value:
        | (typeof ALL_TEAMS_OPTION | Team | string)[]
        | typeof ALL_TEAMS_OPTION
        | Team
        | string
        | null,
      _reason: unknown,
      details?: unknown
    ) => {
      // Ignore non-array changes to avoid clearing selection on open/focus
      if (!value || !Array.isArray(value)) {
        return;
      }

      const typedDetails = details as
        | { option?: typeof ALL_TEAMS_OPTION | Team }
        | undefined;

      if (
        typedDetails?.option &&
        'id' in typedDetails.option &&
        typedDetails.option.id === 'all'
      ) {
        // If all teams are currently selected, clear selection. Otherwise, select all teams.
        // Filter out any "All Teams" option from current selection to get accurate count
        const actualSelectedTeams = value.filter(
          (v): v is Team => typeof v !== 'string' && v.id !== 'all'
        );
        const currentlyAllSelected =
          actualSelectedTeams.length === allTeams.length;
        const newSelection = currentlyAllSelected ? [] : allTeams;
        setTempSelectedTeams(newSelection);
        return;
      }

      const newSelection = value.filter(
        (v): v is Team => typeof v !== 'string' && v.id !== 'all'
      );
      setTempSelectedTeams(newSelection);
    },
    [allTeams]
  );

  const displayText = `${tempSelectedTeams.length} team${tempSelectedTeams.length !== 1 ? 's' : ''} selected`;

  // Include ALL_TEAMS_OPTION in value when all teams are selected
  const allSelected = tempSelectedTeams.length === allTeams.length;
  const displayValue = allSelected
    ? [ALL_TEAMS_OPTION, ...tempSelectedTeams]
    : tempSelectedTeams;

  // Handle close - only persist if selection actually changed
  const handleClose = useCallback(() => {
    const sortIds = (arr: Team[]) =>
      arr
        .map(t => t.id)
        .sort((a, b) => a.localeCompare(b))
        .join(',');
    const next = tempSelectedTeams.length === 0 ? allTeams : tempSelectedTeams;

    const hasChanged = sortIds(next) !== sortIds(selectedTeams);
    if (!hasChanged) {
      // If we temporarily cleared, restore local state without URL writes
      if (tempSelectedTeams.length === 0) {
        setTempSelectedTeams(allTeams);
      }
      return;
    }

    if (tempSelectedTeams.length === 0) {
      setTempSelectedTeams(allTeams);
      updateSelectedTeams(allTeams);
    } else {
      updateSelectedTeams(tempSelectedTeams);
    }
  }, [tempSelectedTeams, selectedTeams, allTeams, updateSelectedTeams]);

  return (
    <MuiThemeProvider theme={MuiDarkTheme}>
      <Box onClick={event => event.stopPropagation()}>
        <Autocomplete
          multiple
          options={options}
          value={displayValue}
          onChange={handleChange}
          clearable={false}
          size="small"
          label="Teams"
          getOptionLabel={option => (option as Team).name ?? 'All Teams'}
          isOptionEqualToValue={(opt, val) =>
            (opt as Team).id === (val as Team).id
          }
          renderValue={() => displayText}
          sx={{ minWidth: 250, width: 250 }}
          {...({ onClose: handleClose } as Record<string, unknown>)}
          data-testid={getTestId({
            action: TestIdAction.MULTI_SELECT,
            object: 'team-select',
            page: 'app-header',
          })}
        />
      </Box>
    </MuiThemeProvider>
  );
}
